/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Pagination, Box } from '@mui/material';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Tooltip from '@mui/material/Tooltip';
import { Link, useSearchParams } from 'react-router-dom';
import { DuplicateIcon, ExportIcon, FaceBookIcon, LinkedIcon, PencilIcons, PintrestIcon, RemarkIcon, TelegramIcon, TwiiterIcon, UtmCostIcon, UtmEyeIcon, UtmGoogleIcon, UtmInstagramIcon, UtmYoutubeIcon, snapchatIcon, redditIcon } from '../../icons';
import UtmLinkModal from './UtmLinkModal';
import RemarksModal from './RemarksModal';
import CreateCampaignModal from './CreateCampaignModal';
import UserTableSkeleton from '../user-management/UserTableSkeleton';
import { buildCampaignLink, formatCurrency, formatRoas, mapCampaignToTableRow } from '../../containers/campaign-container/utils';
import { downloadCsv } from '../../utils/fileUtils';

const CampaignsList = ({
    selectedStatus,
    selectedSources,
    onSearchChange,
    searchText,
    data = [],
    loading = false,
    onEditCampaign,
    onStatusUpdate,
    onCostUpdate,
    onDeleteCampaign,
    sourceOptions = [],
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const renderSourceImage = (src, alt) => (
        <img src={src} alt={alt} style={{ width: 24, height: 24, objectFit: 'contain' }} />
    );

    const [visibleColumns, setVisibleColumns] = useState({
        utmId: true,
        name: true,
        date: true,
        utmlink: true,
        cost: true,
        source: true,
        status: true,
        clicks: true,
        viewed: false,
        signups: true,
        profileCompleted: true,
        ghostUsers: true,
        webuser: true,
        ghostwebusers: true,
        appusers: true,
        ghostappusers: true,
        subscribed: true,
        revenue: true,
        cacsubscribed: true,
        cacprofilecompleted: true,
        roas: true,
        remarks: true,
        action: true,
    });
    const [currentPage, setCurrentPage] = useState(() => {
        const page = Number(searchParams.get('page'));
        return Number.isFinite(page) && page > 0 ? page : 1;
    });
    const [itemsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const tableContainerRef = useRef(null);
    const scrollThumbRef = useRef(null);
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    //status filter

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedStatus, selectedSources, searchText]);
    const [scrollState, setScrollState] = useState({
        scrollLeft: 0,
        scrollWidth: 0,
        clientWidth: 0,
    });

    // Scroll thumb width calculation
    const thumbWidth = useMemo(() => {
        if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
            const ratio = scrollState.clientWidth / scrollState.scrollWidth;
            return Math.max(ratio * scrollState.clientWidth, 50);
        }
        return 100;
    }, [scrollState]);

    // Scroll thumb position
    const thumbPosition = useMemo(() => {
        if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
            const ratio = scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth);
            return ratio * (scrollState.clientWidth - thumbWidth);
        }
        return 0;
    }, [scrollState, thumbWidth]);

    const updateScrollState = () => {
        if (tableContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
            setScrollState({
                scrollLeft,
                scrollWidth,
                clientWidth,
            });
        }
    };

    const handleTableScroll = () => {
        updateScrollState();
    };

    const handleStickyScroll = (e) => {
        if (!tableContainerRef.current) return;

        const track = e.currentTarget;
        const trackRect = track.getBoundingClientRect();
        const clickX = e.clientX - trackRect.left;
        const thumbCenter = thumbWidth / 2;

        const newScrollLeft = (clickX - thumbCenter) / (trackRect.width - thumbWidth) *
            (scrollState.scrollWidth - scrollState.clientWidth);

        tableContainerRef.current.scrollLeft = newScrollLeft;
    };

    const handleThumbDrag = (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startScrollLeft = scrollState.scrollLeft;

        const handleMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const trackWidth = scrollState.clientWidth - thumbWidth;
            const scrollableWidth = scrollState.scrollWidth - scrollState.clientWidth;

            if (trackWidth > 0) {
                const newScrollLeft = startScrollLeft + (deltaX / trackWidth) * scrollableWidth;
                if (tableContainerRef.current) {
                    tableContainerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, scrollableWidth));
                }
            }
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    useEffect(() => {
        updateScrollState();
        window.addEventListener('resize', updateScrollState);

        return () => {
            window.removeEventListener('resize', updateScrollState);
        };
    }, [data, visibleColumns]);


    const handleColumnToggle = (key) => {
        setVisibleColumns((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };
    const filteredData = useMemo(() => {
        const mappedData = data.map((item, index) => mapCampaignToTableRow(item, index));
        return mappedData.filter((item) => {

            //  status filter
            const matchStatus = selectedStatus
                ? item.status?.toLowerCase() === selectedStatus.toLowerCase()
                : true;

            //  source filter
            const matchSource = selectedSources.length > 0
                ? selectedSources.includes(item.source)
                : true;

            //filter for search
            const search = searchText?.toLowerCase() || '';

            const matchSearch =
                item.utmId?.toLowerCase().includes(search) ||
                item.name?.toLowerCase().includes(search) ||
                item.status?.toLowerCase().includes(search) ||
                item.source?.toLowerCase().includes(search) ||
                item.utmlink?.toLowerCase().includes(search) ||
                item.remarks?.toLowerCase().includes(search);

            return matchStatus && matchSource && matchSearch;
        });
    }, [data, selectedStatus, selectedSources, searchText]);
    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    //pagination code start 
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);
    useEffect(() => {
        const total = Math.ceil(filteredData.length / itemsPerPage);
        setTotalPages(total);
    }, [filteredData, itemsPerPage]);

    //pagination code end 
    const columnHeaders = [
        // { key: 'id', label: '#' },
        { key: 'utmId', label: 'UTM ID' },
        { key: 'name', label: 'Campaign Name' },
        { key: 'date', label: 'Created Date' },
        { key: 'utmlink', label: 'UTM Link' },
        { key: 'cost', label: 'Cost' },
        { key: 'source', label: 'Source' },
        { key: 'status', label: 'Status' },
        { key: 'clicks', label: 'Clicks' },
        { key: 'viewed', label: 'Viewed' },
        { key: 'signups', label: 'Signups' },
        { key: 'profileCompleted', label: 'Profile Completed' },
        { key: 'ghostUsers', label: 'Ghost Users' },
        { key: 'webuser', label: 'Web Users' },
        { key: 'ghostwebusers', label: 'Ghost Web Users' },
        { key: 'appusers', label: 'App Users' },
        { key: 'ghostappusers', label: 'Ghost App Users' },
        { key: 'subscribed', label: 'Subscribed' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'cacsubscribed', label: 'CAC Subscribed' },
        { key: 'cacprofilecompleted', label: 'CAC Profile Completed' },
        { key: 'roas', label: 'ROAS' },
        { key: 'remarks', label: 'Remarks' },
        { key: 'action', label: 'Action' },
    ];

    const formatName = (text) => {
        if (!text) return "-";
        const words = text.trim().split(" ");
        if (words.length <= 2) return text;

        return words.slice(0, 2).join(" ") + "...";
    };
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isUtmClicked, setIsUtmClicked] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [costValues, setCostValues] = useState({});
    const [isRemark, setIsRemark] = useState(false);
    const exportHeaders = [
        { key: 'utmId', label: 'UTM ID' },
        { key: 'name', label: 'Campaign Name' },
        { key: 'date', label: 'Created Date' },
        { key: 'utmlink', label: 'UTM Link' },
        { key: 'cost', label: 'Cost' },
        { key: 'source', label: 'Source' },
        { key: 'status', label: 'Status' },
        { key: 'clicks', label: 'Clicks' },
        { key: 'viewed', label: 'Viewed' },
        { key: 'signups', label: 'Signups' },
        { key: 'profileCompleted', label: 'Profile Completed' },
        { key: 'ghostUsers', label: 'Ghost Users' },
        { key: 'webuser', label: 'Web Users' },
        { key: 'ghostwebusers', label: 'Ghost Web Users' },
        { key: 'appusers', label: 'App Users' },
        { key: 'ghostappusers', label: 'Ghost App Users' },
        { key: 'subscribed', label: 'Subscribed' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'cacsubscribed', label: 'CAC Subscribed' },
        { key: 'cacprofilecompleted', label: 'CAC Profile Completed' },
        { key: 'roas', label: 'ROAS' },
        { key: 'remarks', label: 'Remarks' },
    ];

    const handleClickRemarkModal = (item) => {
        setSelectedCampaign(item);
        setIsRemark(true)
    }

    const handleCloseRemarkModal = () => {
        setIsRemark(false)
    }

    const handleClickModal = (item) => {
        setSelectedCampaign(item);
        setIsUtmClicked(true);
    };

    const handleCloseModal = () => {
        setIsUtmClicked(false)
    }

    const getUtmLink = (campaignData) => buildCampaignLink(campaignData);

    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopyText = (index, text) => {
        if (text) {
            navigator.clipboard.writeText(text);
        }
        setCopiedIndex(index);

        setTimeout(() => {
            setCopiedIndex(null);
        }, 1500);
    };

    const handleCostClick = (id) => {
        setEditingIndex(id);
    };

    const handleCostChange = (id, value) => {
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setCostValues((prev) => ({
                ...prev,
                [id]: value,
            }));
        }
    };

    const handleBlur = (id, item) => {
        const newCost = costValues[id];
        if (item && newCost !== undefined && onCostUpdate) {
            onCostUpdate(item, newCost);
        }
        setEditingIndex(null);
    };

    const handleStatusChange = (item, newStatus) => {
        if (!item || !onStatusUpdate) return;
        onStatusUpdate(item, newStatus === "Active");
    };

    const handleExport = () => {
        const headers = exportHeaders.filter((header) => visibleColumns[header.key]);
        const rows = filteredData.map((item) => ({
            ...item,
            cost: item.cost ? formatCurrency(item.cost) : '-',
            revenue: formatCurrency(item.revenue),
            cacsubscribed: formatCurrency(item.cacsubscribed),
            cacprofilecompleted: formatCurrency(item.cacprofilecompleted),
            roas: formatRoas(item.roas),
        }));

        downloadCsv({
            fileName: 'UTM-campaigns.csv',
            headers,
            rows,
        });
    };

    const currentQuery = searchParams.toString();
    const buildUserListUrl = (type, id) => `/user-list-campaign/${type}/${id}${currentQuery ? `?${currentQuery}` : ''}`;

    useEffect(() => {
        const params = new URLSearchParams(searchParams);

        if (currentPage > 1) {
            params.set('page', String(currentPage));
        } else {
            params.delete('page');
        }

        const nextParams = params.toString();
        if (nextParams !== searchParams.toString()) {
            setSearchParams(params, { replace: true });
        }
    }, [currentPage, searchParams, setSearchParams]);

    return (
        <>
            <div className="mb-16 d-flex justify-content-between align-items-center">
                <h6 className="text-lg mb-0">Campaigns</h6>
                <div className="d-flex align-items-center gap-2">
                    <form className="navbar-search">
                        <input
                            type="text"
                            name="search"
                            placeholder="Search"
                            value={searchText}
                            onChange={(e) => onSearchChange(e.target.value)}

                        />
                        <span className="icon">
                            <iconify-icon icon="ion:search-outline"></iconify-icon>
                        </span>
                    </form>

                    <div>
                        <Tooltip title="Export" arrow placement="top">
                            <button
                                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                                type="button"
                                onClick={handleExport}  >
                                <ExportIcon />
                            </button>
                        </Tooltip>
                    </div>

                    <div className="dropdown">
                        <Tooltip title="Manage Columns" arrow placement="top">
                            <button
                                className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <ViewColumnOutlinedIcon />
                            </button>
                        </Tooltip>
                        <ul className="dropdown-menu manage-list">
                            {columnHeaders.map((column) => (
                                <li
                                    key={column.key}
                                    className="dropdown-item d-flex align-items-center gap-2"
                                >
                                    <input
                                        className="form-check-input"
                                        style={{ width: '18px', height: '18px' }}
                                        type="checkbox"
                                        checked={visibleColumns[column.key]}
                                        onChange={() => handleColumnToggle(column.key)}
                                    />
                                    {column.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <div className="table-scroll-container" style={{ overflow: 'visible !impotant' }}>
                <div  
                    ref={tableContainerRef}
                    className="custom-horizontal-scroll scroll-sm tablebtmdropnewclass"
                    onScroll={handleTableScroll}
                     style={{ 
    maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
    overflow: 'auto',
    position: 'relative'
  }}
                >
                    <table className="table bordered-table sm-table mb-0" style={{ position: 'relative' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                                <th>#</th>
                                {visibleColumns.utmId && <th>UTM ID</th>}
                                {visibleColumns.name && <th className="text-center">Campaign Name</th>}
                                {visibleColumns.date && <th className="text-center">Created Date</th>}
                                {visibleColumns.utmlink && <th className="text-center">UTM Link</th>}
                                {visibleColumns.cost && <th className="text-center">Cost</th>}
                                {visibleColumns.source && <th className="text-center">Source</th>}
                                {visibleColumns.status && <th className="text-center">Status</th>}
                                {visibleColumns.clicks && <th className="text-center">Clicks</th>}
                                {visibleColumns.viewed && <th className="text-center">Viewed</th>}
                                {visibleColumns.signups && <th className="text-center">Signups</th>}
                                {visibleColumns.profileCompleted && <th className="text-center">Profile Completed</th>}
                                {visibleColumns.ghostUsers && <th className="text-center">Ghost Users</th>}
                                {visibleColumns.webuser && <th className="text-center">Web Users</th>}
                                {visibleColumns.ghostwebusers && <th className="text-center">Ghost Web Users</th>}
                                {visibleColumns.appusers && <th className="text-center">App Users</th>}
                                {visibleColumns.ghostappusers && <th className="text-center">Ghost App Users</th>}
                                {visibleColumns.subscribed && <th className="text-center">Subscribed</th>}
                                {visibleColumns.revenue && <th className="text-center">Revenue</th>}
                                {visibleColumns.cacsubscribed && <th className="text-center">CAC Subscribed</th>}
                                {visibleColumns.cacprofilecompleted && <th className="text-center">CAC Profile Completed</th>}
                                {visibleColumns.roas && <th className="text-center">ROAS</th>}
                                {visibleColumns.remarks && <th className="text-center">Remarks</th>}
                                {visibleColumns.action && <th className="text-center">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <>
                                    <UserTableSkeleton
                                        rows={10}
                                        columns={Object.keys(visibleColumns).filter((key) => visibleColumns[key]).length + 1}
                                    />
                                </>
                            ) :
                                paginatedData.length > 0 ? (
                                    paginatedData.map((item, index) => (
                                        <tr key={index}>
                                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>

                                            {visibleColumns.utmId && <td>{item.utmId}</td>}
                                            {visibleColumns.name && (
                                                <td className="text-center">
                                                    <Tooltip title={item.name} arrow>
                                                        <span>
                                                            {formatName(item.name)}
                                                        </span>
                                                    </Tooltip>
                                                </td>
                                            )}
                                            {visibleColumns.date && <td className="text-center">{item.date}</td>}
                                            {visibleColumns.utmlink && <td className="text-center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                    <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={() => handleCopyText(index, getUtmLink(item))}>
                                                        <DuplicateIcon />
                                                        {
                                                            copiedIndex === index && (
                                                                <span style={{
                                                                    background: '#00000080', color: '#fff', fontWeight: '400', borderRadius: '40px', padding: '3px 14px', fontSize: '10px',
                                                                    position: 'absolute', top: '-21px', zIndex: '999', left: '-17px'
                                                                }}>
                                                                    Copied
                                                                </span>
                                                            )
                                                        }
                                                    </Box>

                                                    <UtmEyeIcon
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleClickModal(item)}
                                                    />
                                                </Box>
                                            </td>}
                                            {visibleColumns.cost && (
                                                <td className="text-center">
                                                    {editingIndex === item.utmId ? (
                                                        <input
                                                            type="text"
                                                            autoFocus
                                                            value={costValues[item.utmId] !== undefined ? costValues[item.utmId] : (item.cost || "")}
                                                            onChange={(e) => handleCostChange(item.utmId, e.target.value)}
                                                            onBlur={() => handleBlur(item.utmId, item)}
                                                            style={{
                                                                width: "80px",
                                                                padding: "4px",
                                                                border: "1px solid #ccc",
                                                                borderRadius: "4px",
                                                            }}
                                                        />
                                                    ) : item.cost ? (
                                                        <Box
                                                            sx={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
                                                            onClick={() => handleCostClick(item.utmId)}>
                                                            <span>{formatCurrency(item.cost)}</span>
                                                            <PencilIcons />
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{ cursor: "pointer" }}
                                                            onClick={() => handleCostClick(item.utmId)}
                                                        >
                                                            <UtmCostIcon />
                                                        </Box>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.source && (
                                                <td className="text-center">
                                                    <Box>
                                                        {item.source === "Google" && <UtmGoogleIcon />}
                                                        {item.source === "YouTube" && <UtmYoutubeIcon />}
                                                        {item.source === "Telegram" && <TelegramIcon />}
                                                        {item.source === "Facebook" && <FaceBookIcon />}
                                                        {item.source === "Snapchat" && renderSourceImage(snapchatIcon, 'Snapchat')}
                                                        {item.source === "Reddit" && renderSourceImage(redditIcon, 'Reddit')}
                                                        {item.source === "Instagram" && <UtmInstagramIcon />}
                                                        {item.source === "Twitter" && <TwiiterIcon />}
                                                        {item.source === "Pinterest" && <PintrestIcon />}
                                                        {item.source === "LinkedIn" && <LinkedIcon />}
                                                        {!['Google', 'YouTube', 'Telegram', 'Facebook', 'Snapchat', 'Reddit', 'Instagram', 'Twitter', 'Pinterest', 'LinkedIn'].includes(item.source) && (
                                                            <span>{item.source || '-'}</span>
                                                        )}
                                                    </Box>
                                                </td>
                                            )}
                                            {visibleColumns.status && (
                                                <td className="text-center">
                                                    <span
                                                        style={{
                                                            padding: "6px 25px",
                                                            borderRadius: "40px",
                                                            fontSize: "12px",
                                                            fontWeight: "500",
                                                            backgroundColor:
                                                                item.status === "Active" ? "#008E40" : "#FFD67E",
                                                            color:
                                                                item.status === "Active" ? "#fff" : "#3E3E3E",
                                                        }}
                                                    >
                                                        {item.status}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.clicks && <td className="text-center">{item.clicks}</td>}

                                            {visibleColumns.signups && <td className="text-center">
                                                <Link
                                                    to={buildUserListUrl('signups', item._id)}
                                                    style={{ color: '#2E80DB', textDecoration: 'underline' }}
                                                >
                                                    {item.signups}
                                                </Link>
                                            </td>}
                                            {visibleColumns.profileCompleted && <td className="text-center">
                                                <Link
                                                    to={buildUserListUrl('profile-completed', item._id)}
                                                    style={{ color: '#2E80DB', textDecoration: 'underline' }}
                                                >
                                                    {item.profileCompleted}
                                                </Link>
                                            </td>}
                                            {visibleColumns.ghostUsers && <td className="text-center">
                                                <Link
                                                    to={buildUserListUrl('ghost-users', item._id)}
                                                    style={{ color: '#2E80DB', textDecoration: 'underline' }}
                                                >
                                                    {item.ghostUsers}
                                                </Link>
                                            </td>}
                                            {visibleColumns.webuser && <td className="text-center">
                                                <Link
                                                    to={buildUserListUrl('web-users', item._id)}
                                                    style={{ color: '#2E80DB', textDecoration: 'underline' }}
                                                >
                                                    {item.webuser}
                                                </Link>
                                            </td>}
                                            {visibleColumns.ghostwebusers && <td className="text-center">
                                                <Link
                                                    to={buildUserListUrl('ghost-web-users', item._id)}
                                                    style={{ color: '#2E80DB', textDecoration: 'underline' }}
                                                >
                                                    {item.ghostwebusers}
                                                </Link>
                                            </td>}
                                            {visibleColumns.appusers && <td className="text-center">
                                                <Link
                                                    to={buildUserListUrl('app-users', item._id)}
                                                    style={{ color: '#2E80DB', textDecoration: 'underline' }}
                                                >
                                                    {item.appusers}
                                                </Link>
                                            </td>}
                                            {visibleColumns.ghostappusers && <td className="text-center">
                                                <Link
                                                    to={buildUserListUrl('ghost-app-users', item._id)}
                                                    style={{ color: '#2E80DB', textDecoration: 'underline' }}
                                                >
                                                    {item.ghostappusers}
                                                </Link>
                                            </td>}
                                            {visibleColumns.subscribed && <td className="text-center">{item.subscribed}</td>}
                                            {visibleColumns.revenue && <td className="text-center">{formatCurrency(item.revenue)}</td>}
                                            {visibleColumns.cacsubscribed && <td className="text-center">{formatCurrency(item.cacsubscribed)}</td>}
                                            {visibleColumns.cacprofilecompleted && <td className="text-center">{formatCurrency(item.cacprofilecompleted)}</td>}
                                            {visibleColumns.roas && <td className="text-center">{formatRoas(item.roas)}</td>}
                                            {visibleColumns.remarks && <td className="text-center">
                                                <RemarkIcon onClick={() => handleClickRemarkModal(item)} />
                                            </td>}
                                            {visibleColumns.action && (
                                                <td className="text-center">
                                                    <div className="dropdown">
                                                        <button
                                                            className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon text-sm"
                                                            type="button"
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded="false"
                                                        >
                                                            Action
                                                        </button>
                                                        <ul className="dropdown-menu">

                                                            <li>
                                                                <a className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900" onClick={() => {
                                                                    setSelectedData(item);
                                                                    setIsEdit(true);
                                                                    setOpenModal(true);
                                                                }}> Edit </a>
                                                            </li>
                                                            {
                                                                !item?.isActive && <li>
                                                                    <a
                                                                        className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                                                        onClick={() => handleStatusChange(item, "Active")} >
                                                                        Active
                                                                    </a>
                                                                </li>
                                                            }

                                                            {
                                                                item?.isActive && <li>
                                                                    <a
                                                                        className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                                                        onClick={() => handleStatusChange(item, "Paused")}>
                                                                        Paused
                                                                    </a>
                                                                </li>
                                                            }
                                                        </ul>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="20" className="text-center py-3">
                                            No data found
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                    </table>
                </div>

                {scrollState.scrollWidth > scrollState.clientWidth && (
                    <div className="sticky-scroll-container">
                        <div className="sticky-scroll-track" onClick={handleStickyScroll}>
                            <div
                                ref={scrollThumbRef}
                                className="sticky-scroll-thumb"
                                style={{
                                    width: `${thumbWidth}px`,
                                    transform: `translateX(${thumbPosition}px)`
                                }}
                                onMouseDown={handleThumbDrag}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {!loading && Boolean(totalPages) && <div className="d-flex justify-content-center mt-16">
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                   sx={{
    "& .MuiPaginationItem-root": {
      color: "#000", // normal text color
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important", // active bg color
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }}
                />
            </div>}

            {/* //utm link modal */}
            <UtmLinkModal
                open={isUtmClicked}
                onClose={handleCloseModal}
                data={selectedCampaign}
            />

            {/* //remarks modal */}
            <RemarksModal
                open={isRemark}
                onClose={handleCloseRemarkModal}
                data={selectedCampaign}
            />

            <CreateCampaignModal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setIsEdit(false);
                    setSelectedData(null);
                }}
                isEdit={isEdit}
                data={selectedData}
                onSubmit={onEditCampaign}
                sourceOptions={sourceOptions}
            />
        </>
    );
};

export default CampaignsList;
